import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, HelpCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import './KpiHintPopover.css';

export interface KpiHintStep {
  /** Number shown in the badge (matches the number in Quality settings panel). */
  n: number;
  /** Short instruction (one sentence). */
  text: React.ReactNode;
  /** Anchor id inside Quality settings (e.g. "quality-step-2"). */
  anchorId: string;
}

export interface KpiHintPopoverProps {
  /** Stable id (used for aria + per-card state). */
  id: string;
  /** KPI label (also used as popover heading). */
  label: string;
  /** One short paragraph: what the metric measures and why it matters. */
  what: React.ReactNode;
  /** "How it's calculated" bullets (already in JSX <li> form is OK or string array). */
  formula: React.ReactNode[];
  /** Numbered configuration steps (point to QualitySettingsPanel sections). */
  steps: KpiHintStep[];
}

const POPOVER_WIDTH = 360;
const ARROW_INSET = 14;
const VERTICAL_GAP = 12;
const VIEWPORT_PADDING = 12;

interface AnchoredPosition {
  top: number;
  left: number;
  /** Where the popover sits relative to the trigger (drives the arrow). */
  side: 'top' | 'bottom';
  /** Where the arrow points horizontally inside the popover, relative to its left edge. */
  arrowOffset: number;
}

function computePosition(triggerRect: DOMRect): AnchoredPosition {
  const triggerCenterX = triggerRect.left + triggerRect.width / 2;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  const halfW = POPOVER_WIDTH / 2;
  let left = triggerCenterX - halfW;
  if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
  if (left + POPOVER_WIDTH > viewportW - VIEWPORT_PADDING) {
    left = Math.max(VIEWPORT_PADDING, viewportW - VIEWPORT_PADDING - POPOVER_WIDTH);
  }

  const arrowOffset = Math.min(
    POPOVER_WIDTH - ARROW_INSET - 6,
    Math.max(ARROW_INSET, triggerCenterX - left)
  );

  // Default below the trigger; flip above when there's no room.
  const spaceBelow = viewportH - triggerRect.bottom - VIEWPORT_PADDING;
  const spaceAbove = triggerRect.top - VIEWPORT_PADDING;

  // Use a generous estimate for popover height (bounded by viewport later via max-height in CSS).
  const desiredHeight = 480;
  const side: 'top' | 'bottom' =
    spaceBelow >= desiredHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top';

  let top: number;
  if (side === 'bottom') {
    top = triggerRect.bottom + VERTICAL_GAP;
  } else {
    // Pin to trigger top with gap; CSS caps height so it never overflows viewport.
    top = triggerRect.top - VERTICAL_GAP;
  }

  return { top, left, side, arrowOffset };
}

/**
 * Click-to-open hint popover for Quality KPI cards.
 *
 * Rendered through a portal to `document.body` with `position: fixed` so it
 * always paints above siblings, grid items, and any parent stacking contexts
 * (the KPI cards use `backdrop-filter: blur` which would otherwise clip it).
 */
export const KpiHintPopover: React.FC<KpiHintPopoverProps> = ({
  id,
  label,
  what,
  formula,
  steps,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<AnchoredPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setPosition(computePosition(trigger.getBoundingClientRect()));
  }, []);

  // Recompute on open + on scroll/resize so the popover follows the trigger.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // Outside click closes; clicks inside the popover or on the trigger don't.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = triggerRef.current;
      const p = popoverRef.current;
      const target = e.target as Node;
      if ((t && t.contains(target)) || (p && p.contains(target))) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const firstStepAnchor = steps[0]?.anchorId ?? 'quality-settings';
  const settingsHref = `/settings?tab=project&section=${encodeURIComponent(firstStepAnchor)}`;

  const popover =
    open && position ? (
      <div
        id={`kpi-hint-${id}`}
        ref={popoverRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby={`kpi-hint-title-${id}`}
        className={`kpi-hint-popover kpi-hint-popover--${position.side}`}
        style={{
          top: position.side === 'bottom' ? position.top : undefined,
          bottom: position.side === 'top' ? window.innerHeight - position.top : undefined,
          left: position.left,
          width: POPOVER_WIDTH,
          // CSS variable that drives the arrow position inside the popover.
          ['--kpi-hint-arrow-offset' as string]: `${position.arrowOffset}px`,
        }}
      >
        <header className="kpi-hint-popover__header">
          <h4 id={`kpi-hint-title-${id}`} className="kpi-hint-popover__title">
            {label}
          </h4>
          <button
            type="button"
            className="kpi-hint-popover__close"
            aria-label="Close"
            onClick={close}
          >
            <X size={14} />
          </button>
        </header>

        <div className="kpi-hint-popover__scroll">
          <p className="kpi-hint-popover__lead">{what}</p>

          {formula.length > 0 && (
            <section className="kpi-hint-popover__section">
              <h5 className="kpi-hint-popover__subtitle">How it’s calculated</h5>
              <ul className="kpi-hint-popover__bullets">
                {formula.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {steps.length > 0 && (
            <section className="kpi-hint-popover__section">
              <h5 className="kpi-hint-popover__subtitle">Setup checklist</h5>
              <ol className="kpi-hint-popover__steps">
                {steps.map((step) => (
                  <li key={step.n} className="kpi-hint-popover__step">
                    <span className="kpi-hint-popover__step-number" aria-label={`Step ${step.n}`}>
                      {step.n}
                    </span>
                    <span className="kpi-hint-popover__step-text">{step.text}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>

        <footer className="kpi-hint-popover__actions">
          <Link to={settingsHref} className="kpi-hint-popover__cta" onClick={close}>
            Open Quality settings
            <ArrowRight size={14} aria-hidden />
          </Link>
        </footer>
      </div>
    ) : null;

  return (
    <span className="kpi-hint-anchor">
      <button
        ref={triggerRef}
        type="button"
        className="kpi-hint-trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? `kpi-hint-${id}` : undefined}
        aria-label={`How “${label}” is calculated and how to set it up`}
        title={`How “${label}” works`}
        onClick={() => setOpen((o) => !o)}
      >
        <HelpCircle size={14} aria-hidden />
      </button>
      {popover ? createPortal(popover, document.body) : null}
    </span>
  );
};

export default KpiHintPopover;
